import {
  Box,
  Checkbox,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Control, Controller, FieldErrors } from "react-hook-form";

import {
  ALLOWED_MODELS_BY_TYPE,
  ISKUForm,
  modelLabelMap,
} from "../variables/skuData";

interface SKUFormItemProps {
  index: number;
  componentName: string; // read-only, from skuCatalog
  componentType: string; // e.g. "AGX", "CH4", "OPT" — used to derive allowed models
  control: Control<ISKUForm>;
  errors: FieldErrors<ISKUForm>;
}

const typeColors: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  AGX: "secondary",
  CH4: "warning",
  OPT: "primary",
  THM: "error",
  VOC: "success",
  LWOP: "success",
  POE: "info",
};

const SKUFormItem = ({
  index,
  componentName,
  componentType,
  control,
  errors,
}: SKUFormItemProps): JSX.Element => {
  const allowedModels = ALLOWED_MODELS_BY_TYPE[componentType] ?? [];
  const isReadOnly = allowedModels.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 200 }}
      >
        <Chip
          label={componentType}
          size="small"
          color={typeColors[componentType] ?? "default"}
          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
        />
        <Typography variant="body2" fontWeight={500}>
          {componentName}
        </Typography>
      </Box>

      {isReadOnly ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Processing node — no model assignment
        </Typography>
      ) : (
        <Controller
          name={`skuDataConfigurations.${index}.models`}
          control={control}
          rules={{
            validate: value => {
              const valid = (value || []).filter(
                (m: string) => m && m.trim() !== ""
              );

              return valid.length > 0 || "At least one model is required";
            },
          }}
          render={({ field }) => (
            <TextField
              select
              size="small"
              label="Model(s)"
              value={field.value ?? []}
              onChange={event =>
                field.onChange(
                  (event.target.value as unknown as string[]).filter(
                    val => val.trim() !== ""
                  )
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: selected =>
                  (selected as string[])
                    .filter(m => m.trim() !== "")
                    .map(m => modelLabelMap[m] || m)
                    .join(", "),
              }}
              sx={{ minWidth: 240, position: "relative" }}
              error={!!errors?.skuDataConfigurations?.[index]?.models}
              helperText={
                (
                  errors?.skuDataConfigurations?.[index]?.models as
                    | { message?: string }
                    | undefined
                )?.message || " "
              }
              FormHelperTextProps={{
                sx: { position: "absolute", bottom: -22 },
              }}
            >
              {allowedModels.map(model => (
                <MenuItem key={model} value={model}>
                  <Checkbox
                    checked={(field.value ?? []).includes(model)}
                    sx={{ marginRight: 1 }}
                  />
                  {modelLabelMap[model] || model}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}
    </Box>
  );
};

export default SKUFormItem;
